import { useEffect, useState, useMemo } from "react";
import * as Icons from "lucide-react";
import { Search } from "lucide-react";
import { api, CATEGORIES } from "../lib/api";
import MapView from "../components/MapView";
import PlaceCard from "../components/PlaceCard";

export default function MapPage() {
  const [places, setPlaces] = useState([]);
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [focused, setFocused] = useState(null);
  const [showList, setShowList] = useState(false);

  useEffect(() => {
    const params = {};
    if (category !== "all") params.category = category;
    if (q) params.q = q;
    api.get("/places", { params }).then(r => setPlaces(r.data));
  }, [category, q]);

  const promoted = useMemo(() => places.filter(p => p.is_promoted).length, [places]);

  return (
    <div className="relative h-screen w-full overflow-hidden bg-[#FFFBF5]" data-testid="map-page">
      {/* Map full viewport */}
      <div className="absolute inset-0">
        <MapView places={places} focused={focused}/>
      </div>

      {/* Floating filters at top */}
      <div className="absolute top-20 left-0 right-0 z-[500] px-4 sm:px-6 pointer-events-none">
        <div className="max-w-3xl mx-auto pointer-events-auto">
          <div className="rounded-3xl bg-white/95 backdrop-blur-xl border border-white/40 shadow-xl p-3 sm:p-4">
            <div className="relative">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#1A1A1A]/50"/>
              <input
                data-testid="map-search"
                value={q} onChange={(e)=>setQ(e.target.value)}
                placeholder="Cherche sur la carte…"
                className="w-full pl-11 pr-4 h-11 rounded-full bg-[#FAF1E1] focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/40 text-sm font-medium"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto no-scrollbar mt-3">
              <button data-testid="map-cat-all" onClick={()=>setCategory("all")}
                className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-bold transition-all ${category==="all" ? "bg-[#1A1A1A] text-white" : "bg-[#FAF1E1] text-[#1A1A1A]/70"}`}>Tout</button>
              {CATEGORIES.map(c => {
                const Icon = Icons[c.icon] || Icons.Sparkles;
                const active = category === c.id;
                return (
                  <button key={c.id} data-testid={`map-cat-${c.id}`} onClick={()=>setCategory(c.id)}
                    className={`shrink-0 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-all ${active ? "text-white" : "bg-[#FAF1E1] text-[#1A1A1A]/70 hover:bg-[#F4E1C5]"}`}
                    style={active ? { background: c.color } : {}}>
                    <Icon size={12}/>{c.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Results badge bottom-left */}
      <div className="absolute bottom-6 left-4 sm:left-6 z-[500] backdrop-blur-md bg-white/95 rounded-full px-4 py-2 text-xs font-bold shadow-lg border border-white/50">
        {places.length} spots · {promoted} sponsorisés
      </div>

      {/* Toggle list bottom-right */}
      <button data-testid="toggle-list" onClick={() => setShowList(!showList)}
        className="absolute bottom-6 right-4 sm:right-6 z-[500] rounded-full bg-gradient-to-r from-[#FF6B35] to-[#E84A1F] text-white font-bold py-3 px-5 shadow-xl shadow-[#FF6B35]/30 hover:scale-105 transition-transform text-sm">
        {showList ? "Carte" : `Liste (${places.length})`}
      </button>

      {/* Slide-up list panel */}
      {showList && (
        <div data-testid="map-list" className="absolute bottom-0 left-0 right-0 z-[600] h-[60vh] bg-white rounded-t-3xl shadow-2xl overflow-hidden flex flex-col">
          <div className="p-4 border-b border-black/5 flex items-center justify-between">
            <h3 className="font-display font-bold">Tous les spots</h3>
            <button onClick={() => setShowList(false)} className="text-sm text-[#1A1A1A]/60">Fermer</button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {places.map((p, i) => (
              <PlaceCard key={p.id} place={p} index={i} compact onClick={(pl)=>{ setFocused(pl); setShowList(false); }}/>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
